import React, { useEffect, useState } from 'react';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { roleLabels } from '@/lib/auth';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

export default function UsersAdminPage() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get('/users').then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title="Users" subtitle="Manage users and their roles in this organisation."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus size={14} className="mr-1" /> New user</Button></DialogTrigger>
            <NewUserDialog onCreated={() => { setOpen(false); load(); }} onCancel={() => setOpen(false)} />
          </Dialog>
        }
      />
      {!users ? <PageLoader /> : users.length === 0 ? <EmptyState title="No users" /> : (
        <Card className="p-0 divide-y divide-border">
          {users.map(u => (
            <div key={u.id} className="flex items-center gap-3 p-3 hover:bg-secondary/50" data-testid={`user-row-${u.email}`}>
              <div className="h-8 w-8 rounded-full bg-primary/20 text-primary text-sm font-semibold flex items-center justify-center">
                {u.full_name.slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{u.full_name}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </div>
              <Badge variant="outline" className="text-[10px]">{roleLabels[u.role] || u.role}</Badge>
              <Badge variant={u.status === 'active' ? 'default' : 'destructive'} className="text-[10px]">{u.status}</Badge>
              <Button variant="ghost" size="sm" onClick={() => del(u)}><Trash2 size={14} className="text-red-500" /></Button>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function NewUserDialog({ onCreated, onCancel }) {
  const [f, setF] = useState({ email: '', full_name: '', role: 'analyst', password: 'Welcome@1234', phone: '', department: '' });
  const submit = async () => {
    try {
      await api.post('/users', f);
      toast.success('User created');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create user'); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-2">
        <div><Label>Full name</Label><Input value={f.full_name} onChange={e => setF({ ...f, full_name: e.target.value })} data-testid="user-form-name" /></div>
        <div><Label>Email</Label><Input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="user-form-email" /></div>
        <div><Label>Role</Label>
          <Select value={f.role} onValueChange={v => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="org_admin">Organisation Administrator</SelectItem>
              <SelectItem value="asset_manager">Asset Manager</SelectItem>
              <SelectItem value="analyst">Analyst</SelectItem>
              <SelectItem value="field_user">Field User</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Temporary password</Label><Input value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="user-form-password" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
          <div><Label>Department</Label><Input value={f.department} onChange={e => setF({ ...f, department: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={!f.email || !f.full_name} data-testid="user-form-submit">Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}
